import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { Plus, Trash2, Save, Loader2, AlertCircle, X, Upload, Edit3, Image as ImageIcon, Table as TableIcon } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CHART = {
  school_id: '',
  fit: 'adult',
  title: '',
  chart_type: 'image', // 'image' | 'table'
  image_url: '',
  table_data: { columns: ['Size', 'Chest', 'Length'], rows: [['', '', '']] },
  notes: '',
  sort_order: 0,
  is_active: true,
};

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(d);
};

const AdminSizingCharts = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [charts, setCharts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null); // current chart being edited (or new draft)
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/api/admin/sizing-charts`, { headers }),
        axios.get(`${API}/api/categories/schools`),
      ]);
      setCharts(c.data || []);
      setSchools(s.data || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const startCreate = () => {
    const firstSchool = schools[0]?.id || '';
    setEditing({ ...EMPTY_CHART, school_id: firstSchool });
    setShowForm(true);
  };

  const startEdit = (chart) => {
    setEditing({
      ...EMPTY_CHART,
      ...chart,
      // Ensure table_data has the new structure if loaded from DB
      table_data: chart.table_data || { columns: ['Size', 'Chest', 'Length'], rows: [['', '', '']] },
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const save = async () => {
    if (!editing.school_id) { toast.error('Pick a school'); return; }
    if (!editing.fit?.trim()) { toast.error('Fit is required'); return; }
    if (!editing.title?.trim()) { toast.error('Title is required'); return; }
    try {
      const payload = {
        school_id: editing.school_id,
        fit: editing.fit.trim(),
        title: editing.title.trim(),
        chart_type: editing.chart_type,
        image_url: editing.chart_type === 'image' ? (editing.image_url || null) : null,
        table_data: editing.chart_type === 'table' ? editing.table_data : null,
        notes: editing.notes || null,
        sort_order: editing.sort_order || 0,
        is_active: editing.is_active !== false,
      };
      if (editing.id) {
        await axios.put(`${API}/api/admin/sizing-charts/${editing.id}`, payload, { headers });
        toast.success('Saved');
      } else {
        await axios.post(`${API}/api/admin/sizing-charts`, payload, { headers });
        toast.success('Chart created');
      }
      closeForm();
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (chart) => {
    if (!window.confirm(`Delete "${chart.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/sizing-charts/${chart.id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/api/uploads/admin`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, image_url: r.data.public_url || r.data.url }));
      toast.success('Image uploaded');
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Group charts by school for cleaner browsing.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const c of charts) {
      const k = c.school_id;
      if (!m.has(k)) m.set(k, { school_id: k, school_name: c.school_name || '—', items: [] });
      m.get(k).items.push(c);
    }
    return Array.from(m.values());
  }, [charts]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sizing charts...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div className="w-full min-w-0" data-testid="admin-sizing-charts">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">Sizing Charts</h1>
            <p className="text-sm text-gray-500 mt-0.5">Per school, per fit. Upload an image or enter a structured size table.</p>
          </div>
          <button onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5A623] text-[#1B3A5C] font-bold text-sm rounded-full hover:bg-[#D88F1B]"
            data-testid="add-chart-btn">
            <Plus className="w-4 h-4" /> Add Chart
          </button>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{err}</span>
          </div>
        )}

        {grouped.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-2xl border border-gray-100">
            <p className="text-sm text-gray-500">No sizing charts yet. Click <strong>Add Chart</strong> to create one.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((g) => (
              <div key={g.school_id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`school-group-${g.school_id}`}>
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                  <h2 className="text-sm font-bold text-[#1B3A5C] uppercase tracking-wider">{g.school_name}</h2>
                </div>
                <div className="divide-y divide-gray-50">
                  {g.items.map((c) => (
                    <div key={c.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`chart-row-${c.id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="px-2 py-0.5 rounded-md bg-[#F5A623]/10 text-[#F5A623] text-[11px] font-bold uppercase tracking-wider">{c.fit}</span>
                        {c.chart_type === 'image' ? (
                          <ImageIcon className="w-4 h-4 text-gray-400" />
                        ) : (
                          <TableIcon className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="text-sm font-medium text-gray-800 truncate">{c.title}</span>
                        {!c.is_active && (
                          <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold">HIDDEN</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(c)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                          data-testid={`edit-chart-${c.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => remove(c)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                          data-testid={`delete-chart-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && editing && (
        <SizingChartFormModal
          editing={editing}
          setEditing={setEditing}
          schools={schools}
          onClose={closeForm}
          onSave={save}
          onUploadImage={uploadImage}
        />
      )}
    </AdminLayout>
  );
};

const SizingChartFormModal = ({ editing, setEditing, schools, onClose, onSave, onUploadImage }) => {
  const isEdit = !!editing.id;
  const td = editing.table_data || { columns: [], rows: [] };

  const setField = (patch) => setEditing((e) => ({ ...e, ...patch }));

  const setTable = (patch) => setEditing((e) => ({ ...e, table_data: { ...e.table_data, ...patch } }));

  const setColumn = (idx, val) => {
    const cols = [...(td.columns || [])];
    cols[idx] = val;
    setTable({ columns: cols });
  };

  const addColumn = () => {
    setTable({
      columns: [...(td.columns || []), ''],
      rows: (td.rows || []).map((r) => [...r, '']),
    });
  };

  const removeColumn = (idx) => {
    setTable({
      columns: (td.columns || []).filter((_, i) => i !== idx),
      rows: (td.rows || []).map((r) => r.filter((_, i) => i !== idx)),
    });
  };

  const setCell = (rowIdx, colIdx, val) => {
    const rows = (td.rows || []).map((r) => [...r]);
    rows[rowIdx][colIdx] = val;
    setTable({ rows });
  };

  const addRow = () => {
    setTable({ rows: [...(td.rows || []), (td.columns || []).map(() => '')] });
  };

  const removeRow = (idx) => {
    setTable({ rows: (td.rows || []).filter((_, i) => i !== idx) });
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) onUploadImage(f);
    e.target.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="chart-form-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#1B3A5C]">{isEdit ? 'Edit Sizing Chart' : 'New Sizing Chart'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600" data-testid="chart-form-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldLabel label="School *">
              <select value={editing.school_id}
                onChange={(e) => setField({ school_id: e.target.value })}
                className={inputCls} data-testid="chart-school">
                <option value="">— Select school —</option>
                {schools.map((s) => (<option key={s.id} value={s.id}>{s.school_name || s.name}</option>))}
              </select>
            </FieldLabel>
            <FieldLabel label="Fit *" hint="e.g. youth, adult, ladies, child, men's">
              <input value={editing.fit} onChange={(e) => setField({ fit: e.target.value })}
                className={inputCls} placeholder="adult" data-testid="chart-fit" />
            </FieldLabel>
          </div>

          <FieldLabel label="Title *" hint="Shown to customers on the product page (e.g. 'Adult Polo Sizing').">
            <input value={editing.title} onChange={(e) => setField({ title: e.target.value })}
              className={inputCls} placeholder="Adult Polo Sizing" data-testid="chart-title" />
          </FieldLabel>

          <FieldLabel label="Chart type">
            <div className="flex gap-2">
              {[{ v: 'image', label: 'Image' }, { v: 'table', label: 'Table' }].map((opt) => {
                const active = editing.chart_type === opt.v;
                return (
                  <button key={opt.v} type="button" onClick={() => setField({ chart_type: opt.v })}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-medium ${active ? 'border-[#F5A623] bg-[#F5A623]/5 text-[#1B3A5C]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid={`chart-type-${opt.v}`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </FieldLabel>

          {editing.chart_type === 'image' ? (
            <FieldLabel label="Image">
              <div className="space-y-2">
                <input type="text" value={editing.image_url || ''}
                  onChange={(e) => setField({ image_url: e.target.value })}
                  placeholder="https://... or upload below"
                  className={inputCls} data-testid="chart-image-url" />
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm">
                  <Upload className="w-4 h-4" /> Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="chart-image-upload" />
                </label>
                {editing.image_url && (
                  <img src={editing.image_url} alt="preview"
                    className="max-h-72 rounded-lg border border-gray-100" />
                )}
              </div>
            </FieldLabel>
          ) : (
            <FieldLabel label="Table">
              <div className="rounded-lg border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      {(td.columns || []).map((col, i) => (
                        <th key={i} className="px-2 py-1.5 text-left">
                          <div className="flex items-center gap-1">
                            <input value={col} onChange={(e) => setColumn(i, e.target.value)}
                              className="w-full px-2 py-1 rounded border border-gray-200 text-xs font-bold"
                              placeholder={i === 0 ? 'Size' : 'Column'}
                              data-testid={`chart-col-${i}`} />
                            <button type="button" onClick={() => removeColumn(i)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Remove column"
                              data-testid={`chart-col-remove-${i}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </th>
                      ))}
                      <th className="px-2 py-1.5">
                        <button type="button" onClick={addColumn}
                          className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                          data-testid="chart-col-add">
                          <Plus className="w-3 h-3 inline -mt-0.5" /> Col
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(td.rows || []).map((row, ri) => (
                      <tr key={ri} className="border-b border-gray-50">
                        {(td.columns || []).map((_, ci) => (
                          <td key={ci} className="px-2 py-1">
                            <input value={row[ci] || ''}
                              onChange={(e) => setCell(ri, ci, e.target.value)}
                              className="w-full px-2 py-1 rounded border border-gray-200 text-xs"
                              data-testid={`chart-cell-${ri}-${ci}`} />
                          </td>
                        ))}
                        <td className="px-2 py-1">
                          <button type="button" onClick={() => removeRow(ri)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            data-testid={`chart-row-remove-${ri}`}>
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-2 py-2 border-t border-gray-100">
                  <button type="button" onClick={addRow}
                    className="px-3 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                    data-testid="chart-row-add">
                    <Plus className="w-3 h-3 inline -mt-0.5" /> Add row
                  </button>
                </div>
              </div>
            </FieldLabel>
          )}

          <FieldLabel label="Notes" hint="Optional caption shown under the chart.">
            <textarea rows={2} value={editing.notes || ''}
              onChange={(e) => setField({ notes: e.target.value })}
              className={inputCls} data-testid="chart-notes" />
          </FieldLabel>

          <div className="grid sm:grid-cols-2 gap-3">
            <FieldLabel label="Sort order" hint="Lower numbers appear first.">
              <input type="number" min="0" step="1" value={editing.sort_order || 0}
                onChange={(e) => setField({ sort_order: parseInt(e.target.value, 10) || 0 })}
                className={inputCls} data-testid="chart-sort" />
            </FieldLabel>
            <FieldLabel label="Active">
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={editing.is_active !== false}
                  onChange={(e) => setField({ is_active: e.target.checked })}
                  className="accent-[#F5A623]" data-testid="chart-active" />
                <span className="text-sm text-gray-700">Show this chart to customers</span>
              </label>
            </FieldLabel>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={onSave}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] hover:bg-[#D88F1B] text-sm font-bold"
            data-testid="chart-save-btn">
            <Save className="w-4 h-4" /> {isEdit ? 'Save changes' : 'Create chart'}
          </button>
        </div>
      </div>
    </div>
  );
};

const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 text-sm";

const FieldLabel = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

export default AdminSizingCharts;
